import { createBaseStore } from '@tasama/utils';
import type { BaseStoreState, StoreActions } from '@tasama/utils';

/**
 * New Ticket Drawer Store using Zustand with @tasama/utils utilities
 * Manages state for the global NewTicketDrawer component
 */

interface NewTicketDrawerCustomState {
  // State
  isOpen: boolean;
  onSuccessCallback:
    | ((_ticketNumber: string, _ticketData?: unknown) => void)
    | null;
  serviceId: string | undefined;
  serviceL1: string | undefined;
  serviceL2: string | undefined;
  serviceL3: string | undefined;
  requestId: string | undefined;
  serviceName: string | undefined;
  srAttemptId: string | undefined;
  parentSrId: string | undefined;
  serviceStatus: string | undefined;
  /** TDF-1342: bundle-side equivalent of srAttemptId — pre-selects the "Requested Service" topic's subject when opened from a bundle context (embedded bundle-detail Support Tickets tab). */
  bundleAttemptId: string | undefined;
}

interface NewTicketDrawerCustomActions {
  // Actions
  open: (
    _onSuccess?: (_ticketNumber: string, _ticketData?: unknown) => void,
    _serviceId?: string,
    _serviceL1?: string,
    _serviceL2?: string,
    _serviceL3?: string,
    _requestId?: string,
    _serviceName?: string,
    _srAttemptId?: string,
    _parentSrId?: string,
    _serviceStatus?: string,
    _bundleAttemptId?: string
  ) => void;
  close: () => void;
  triggerSuccess: (_ticketNumber: string, _ticketData?: unknown) => void;
}

type NewTicketDrawerState = NewTicketDrawerCustomState &
  BaseStoreState &
  NewTicketDrawerCustomActions;

const initialState: Omit<
  NewTicketDrawerState,
  keyof BaseStoreState | keyof StoreActions
> = {
  isOpen: false,
  onSuccessCallback: null,
  serviceId: undefined,
  serviceL1: undefined,
  serviceL2: undefined,
  serviceL3: undefined,
  requestId: undefined,
  serviceName: undefined,
  srAttemptId: undefined,
  parentSrId: undefined,
  serviceStatus: undefined,
  bundleAttemptId: undefined,
  open: () => {},
  close: () => {},
  triggerSuccess: () => {},
};

/**
 * Zustand store for managing NewTicketDrawer state globally
 *
 * This store provides centralized state management for the NewTicketDrawer component,
 * allowing it to be controlled from anywhere in the application.
 *
 * @example
 * ```tsx
 * import { useNewTicketDrawerStore } from '@/features/support-ticketing/store';
 *
 * function MyComponent() {
 *   const { open } = useNewTicketDrawerStore();
 *
 *   const handleOpenDrawer = () => {
 *     open((ticketNumber, ticketData) => {
 *       console.log('Ticket created:', ticketNumber, ticketData);
 *       // Handle success - e.g., show notification, navigate, etc.
 *     });
 *   };
 *
 *   return <button onClick={handleOpenDrawer}>Create New Ticket</button>;
 * }
 * ```
 */
export const useNewTicketDrawerStore = createBaseStore<NewTicketDrawerState>(
  initialState,
  (set, get) => ({
    // Actions
    open: (
      onSuccess?: (_ticketNumber: string, _ticketData?: unknown) => void,
      serviceId?: string,
      serviceL1?: string,
      serviceL2?: string,
      serviceL3?: string,
      requestId?: string,
      serviceName?: string,
      srAttemptId?: string,
      parentSrId?: string,
      serviceStatus?: string,
      bundleAttemptId?: string
    ) => {
      set({
        isOpen: true,
        onSuccessCallback: onSuccess || null,
        serviceId,
        serviceL1,
        serviceL2,
        serviceL3,
        requestId,
        serviceName,
        srAttemptId,
        parentSrId,
        serviceStatus,
        bundleAttemptId,
      } as Partial<NewTicketDrawerState>);
    },

    close: () => {
      set({
        isOpen: false,
        onSuccessCallback: null,
        serviceId: undefined,
        serviceL1: undefined,
        serviceL2: undefined,
        serviceL3: undefined,
        requestId: undefined,
        serviceName: undefined,
        srAttemptId: undefined,
        parentSrId: undefined,
        serviceStatus: undefined,
        bundleAttemptId: undefined,
      } as Partial<NewTicketDrawerState>);
    },

    triggerSuccess: (ticketNumber: string, ticketData?: unknown) => {
      const { onSuccessCallback, close } = get();
      if (onSuccessCallback) {
        onSuccessCallback(ticketNumber, ticketData);
      }
      close();
    },
  })
);

// Export store selectors for easy access
export const useDrawerIsOpen = () =>
  useNewTicketDrawerStore((state) => state.isOpen);
export const useDrawerLoading = () =>
  useNewTicketDrawerStore((state) => state.loading);
export const useDrawerError = () =>
  useNewTicketDrawerStore((state) => state.error);
